import { FC, useEffect, useState } from 'react'
import { positions, useAlert } from 'react-alert'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { onSetEmpty } from '@/core/services/App/AppSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Field } from '@/components/Form/Field'
import { Button } from '@/components/UI/Button'
import { Card } from '@/components/UI/Card'
import Icon from '@/components/UI/Icon'
import { Loading } from '@/components/UI/Loading'
import { Title } from '@/components/UI/Title'

import './Affiliate.scss'
import { affiliateSelector } from './store/AffiliateSelector'
import { getAffiliateThunk } from './store/AffiliateSlice'

const cnAffiliate = cn('Affiliate')
export const Affiliate: FC = () => {
  const alert = useAlert()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const affiliate = useAppSelector(affiliateSelector)

  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    getAffiliate()
    dispatch(onSetEmpty(true))

    return () => {
      dispatch(onSetEmpty(false))
    }
  }, [])

  const getAffiliate = async () => {
    try {
      await dispatch(getAffiliateThunk())
      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const onClick = () => {
    if (!affiliate) return

    alert.show(`${t('affiliate-alert-title')}|${t('affiliate-alert-text')}`, {
      type: 'success',
      position: positions.BOTTOM_RIGHT,
    })
  }

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <>
          {affiliate && (
            <div className={cnAffiliate()}>
              <Card className={cnAffiliate('card')}>
                <Title>{t('affiliate-title-left')}</Title>
                <div className={cnAffiliate('desc')}>{t('affiliate-text')}</div>

                <div className={cnAffiliate('wrap')}>
                  <div className={cnAffiliate('block')}>
                    <div className={cnAffiliate('number')}>01</div>
                    <div className={cnAffiliate('desc')}>
                      {t('affiliate-info-left')}
                    </div>
                  </div>
                  <div className={cnAffiliate('block')}>
                    <div className={cnAffiliate('number')}>02</div>
                    <div className={cnAffiliate('desc')}>
                      {t('affiliate-info-right')}
                    </div>
                  </div>
                </div>
                <Field
                  name={'link'}
                  label={t('affiliate-label')}
                  value={affiliate?.link}
                  disabled
                  mb='none'
                  iconLeft={<Icon name='referral' />}
                />
                <CopyToClipboard text={affiliate?.link} onCopy={onClick}>
                  <Button>{t('affiliate-alert-title')}</Button>
                </CopyToClipboard>
              </Card>
              <Card className={cnAffiliate('card')}>
                <Title>{t('affiliate-title-right')}</Title>

                <div className={cnAffiliate('balance')}>
                  {affiliate.all_profit} USDT
                </div>

                <div className={cnAffiliate('list')}>
                  <div className={cnAffiliate('item')}>
                    <div className={cnAffiliate('label')}>
                      {t('affiliate-item-1')}
                    </div>
                    <div className={cnAffiliate('value')}>100 USDT</div>
                  </div>
                  <div className={cnAffiliate('item')}>
                    <div className={cnAffiliate('label')}>
                      {t('affiliate-item-2')}
                    </div>
                    <div className={cnAffiliate('value')}>
                      {affiliate.all_profit} USDT
                    </div>
                  </div>
                  <div className={cnAffiliate('item')}>
                    <div className={cnAffiliate('label')}>
                      {t('affiliate-item-3')}
                    </div>
                    <div className={cnAffiliate('value')}>
                      {affiliate.referrals_count}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </>
  )
}
